import { initContract } from '@ts-rest/core';
import { z } from 'zod';
const c = initContract();
/**
 * The current authenticated user. Populated from a VERIFIED Keycloak token
 * (issuer → tenant) after the JIT upsert. Roles come from
 * `resource_access.arcreator.roles` (client roles), not `realm_access`.
 */
export const MeSchema = z.object({
    id: z.number().int(),
    uuid: z.string(), // OIDC `sub` is opaque — not spec-guaranteed a UUID (Keycloak's happens to be one)
    email: z.string().email().nullable(),
    realm: z.string(),
    roles: z.array(z.string()),
    /**
     * Raw name parts from the IdP. **Both are usually absent** — of 102 seeded xr-area users only 27 carry either —
     * so a client that renders `${firstName} ${lastName}` shows blank for ~74% of people. Use `displayName`.
     */
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    /**
     * The name to SHOW. Resolved server-side (`name` claim → first + last → username) so the React UI, Unity and
     * mobile render a person identically; the non-TS clients get no compiler help and would each invent a different
     * fallback. **Guaranteed non-empty** — clients may render it directly without a null check.
     */
    displayName: z.string().min(1),
    /**
     * The caller holds a role whose `role` row carries `super_admin` — the SAME source of truth every super-gated
     * read uses, resolved server-side so no client re-derives it from role NAMES.
     *
     * A pure UI signal: it hides affordances the server would refuse anyway (ADR-040's sensor-usage switch is the
     * first, and the workspace's super-only rows are next). Every super-gated WRITE re-checks independently — this is
     * never the authorization. Anticipated by ADR-032's plan as "MeSchema.isSuper, a small API addition when the
     * super-only rows need a client signal"; ADR-040 Phase 2 is when they did.
     */
    isSuper: z.boolean(),
    /**
     * May the caller use the project-management board at `/pm` (ADR-055)? A UI-only signal like `isSuper`: it picks the
     * screen, `PmBoardService` decides on every route.
     */
    pmAccess: z.boolean(),
});
const ErrorSchema = z.object({ message: z.string() });
/**
 * Structured 403 for a host/token tenant mismatch. `code` is stable so clients can branch on it without parsing
 * prose — the UI clears tenant state and re-authenticates against the host's realm rather than showing a generic
 * error. See `auth-tenancy.md` (§ "if the authenticated token's issuer ≠ the tenant expected from the host").
 */
export const TenantMismatchSchema = z.object({
    message: z.string(),
    code: z.literal("TENANT_CONTEXT_MISMATCH"),
    /** The realm this origin serves. The token's realm is deliberately NOT echoed back. */
    expectedRealm: z.string(),
});
/**
 * One Zod contract, all consumers: the React UI gets a typed ts-rest client,
 * Unity/mobile get REST + OpenAPI generated from the same source.
 */
export const contract = c.router({
    getMe: {
        method: 'GET',
        path: '/me',
        responses: {
            200: MeSchema,
            401: ErrorSchema,
            /**
             * The token is VALID but belongs to a different tenant than this origin serves. Not a data-leak fix — RLS
             * already scopes every query to the token's own realm — but a contextual-integrity one: without it a user
             * could edit organization A's content inside organization B's branding.
             */
            403: TenantMismatchSchema,
        },
        summary: 'Current authenticated user (verified token → tenant → JIT upsert).',
    },
});
//# sourceMappingURL=me.contract.js.map